import {
	Avatar,
	Button,
	Link,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownTrigger,
	Tooltip,
	useDisclosure, ModalContent, ModalBody, Modal
} from "@heroui/react";
import {useRecoilState} from 'recoil';
import {userState, sidebarExpandedState} from '../store.jsx';
import {twMerge} from 'tailwind-merge';
import {useMounted, useViewportSize, useWindowScroll} from '@mantine/hooks';
import {useLocation, useNavigate} from 'react-router-dom';
import _, {includes} from 'lodash';
import moment from 'moment';
import {useEffect, useMemo, useState} from 'react';
import {animate, motion, useMotionValue, useTransform} from 'framer-motion';
import {DotLottieReact} from "@lottiefiles/dotlottie-react";
import {clearSessionData} from '../helpers/AppHelper.jsx';

/* Persists across route changes so the counter resumes from the last value instead of re-counting from 0 */
let lastCreditValue = 0;

/* Counts up on the initial load (0 → value) and whenever the value actually changes; no re-animation on navigation */
function CreditCounter({value = 0}) {
	const mv = useMotionValue(lastCreditValue);
	const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());

	useEffect(() => {
		const controls = animate(mv, value, {
			duration: 0.9,
			ease: [0.16, 1, 0.3, 1],
		});
		lastCreditValue = value;
		return controls.stop;
	}, [value, mv]);

	return <motion.strong className="text-md font-semibold tabular-nums text-slate-800">{rounded}</motion.strong>;
}

/* Compact, responsive trial-status pill — lives in the account cluster.
   Full pill on md+, icon-only (with tooltip) on small screens. */
function TrialStatusPill({ status, endDate, animateIn }) {
	if (status !== 'active' && status !== 'expired') return null;
	const expired = status === 'expired';
	const dateStr = moment(endDate || new Date()).format('MMM DD, YYYY');
	const title = expired ? 'Trial expired' : 'Trial active';
	const detail = expired
		? `Trial ended on ${dateStr}. Upgrade to regain access to all features.`
		: `Trial ends on ${dateStr}. Upgrade now to keep enjoying all features.`;

	const tone = expired
		? { bg: 'bg-rose-50', ring: 'ring-rose-200', text: 'text-rose-600', icon: 'text-rose-500', dot: 'bg-rose-500', btn: 'danger' }
		: { bg: 'bg-amber-50', ring: 'ring-amber-200', text: 'text-amber-700', icon: 'text-amber-500', dot: 'bg-amber-500', btn: 'warning' };

	return (
		<>
			{/* Full pill — md+ */}
			<motion.div
				initial={animateIn ? { opacity: 0, scale: 0.96 } : false}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ type: 'spring', stiffness: 300, damping: 24 }}
				className={twMerge('hidden md:flex items-center gap-2.5 rounded-full pl-3 pr-[9px] py-[7px] ring-1', tone.bg, tone.ring)}
			>
				<span className="relative flex h-2 w-2 ml-0.5">
					{!expired && <span className={twMerge('absolute inline-flex h-full w-full animate-ping rounded-full opacity-70', tone.dot)} />}
					<span className={twMerge('relative inline-flex h-2 w-2 rounded-full', tone.dot)} />
				</span>
				<div className="leading-tight">
					<p className={twMerge('text-[11.5px] font-semibold', tone.text)}>{title}</p>
					<p className="text-[10px] text-slate-500 whitespace-nowrap">{expired ? `Ended ${dateStr}` : `Ends ${dateStr}`}</p>
				</div>
				<Button as={Link} href="/user/plans" size="sm" radius="full" color={tone.btn}
					className="h-8 px-3.5 text-tiny font-semibold">
					Upgrade
				</Button>
			</motion.div>

			{/* Compact icon — below md, taps through to plans */}
			<Tooltip className="md:hidden" color="foreground" showArrow
				content={<div className="max-w-72 py-2 px-3 text-center">
					<h3 className="font-bold text-small">{title}</h3>
					<p className="text-tiny mt-1 text-slate-500">{detail}</p>
				</div>}>
				<Button as={Link} href="/user/plans" isIconOnly radius="full" variant="flat"
					className={twMerge('md:hidden !h-[46px] !w-[46px] min-w-0', tone.bg, tone.icon)} aria-label={title}>
					<i className="hgi hgi-stroke hgi-alert-02 text-lg" />
				</Button>
			</Tooltip>
		</>
	);
}

/* Persists across route changes so the entrance animation only plays on the first mount of the session */
let headerHasAnimated = false;

export default function Header({leftArea, currentPage, ...props}) {
	const {isOpen:buyPaidPlanModalIsOpen,  onOpenChange:buyPaidPlanModalOnOpenChange} = useDisclosure()
	const router = useLocation();
	const [authMe] = useRecoilState(userState)
	const [sidebarExpanded, setSidebarExpanded] = useRecoilState(sidebarExpandedState)
	const [animateIn] = useState(() => {
		if (headerHasAnimated) return false;
		headerHasAnimated = true;
		return true;
	});
	const {width, height} = useViewportSize();
	const navigate = useNavigate();
	const onMounted = useMounted()
	const {isOpen, onOpenChange} = useDisclosure()

	const [windowScrollPosition] = useWindowScroll()
	
	const onTabChanged = (tabKey) => {
		if (onMounted) {
			_.isEmpty(tabKey) ? navigate('/app') : navigate(`/app/${tabKey}`)
		}
	}

	const getPageName = useMemo(() => {
		const path = router?.pathname || '';
		if (path.startsWith('/auto-dub')) return 'Auto-Dub'
		if (path.startsWith('/channels')) return 'Channels'
		switch (_.snakeCase(router?.pathname)) {
			case 'app':
				return 'Create Dub'
			case 'voices':
				return 'Voices'
			case 'my_voices':
				return 'My voices'
			case 'text_to_speech':
				return 'AI Speech'
			case 'user_plans':
				return 'Plans'
			case 'user_profile':
				return 'My profile'
			case 'voices_cloning':
				return 'Voice Cloning'
			case 'contact_us':
				return 'Contact Us'
		}
	}, [_.startCase(router?.pathname)])
	const [isVisible, setIsVisible] = useState(true);

	const onVoiceCloneClickHandler = () =>{
		if(includes([3, 5, 7], authMe?.current_plan))
		{
			navigate("/voices-cloning")
		}
		else {
			buyPaidPlanModalOnOpenChange()
		}
	}
	
	return (
		<>
			<div
				className="sticky top-0 right-0 py-3 w-full z-40">
				<motion.header
					initial={animateIn ? {opacity: 0, y: -16} : false}
					animate={{opacity: 1, y: 0}}
					transition={{type: "spring", stiffness: 260, damping: 26}}
					className={twMerge([
						"py-3 flex justify-between items-center px-4 mx-auto transition-all duration-300",
						"rounded-full z-50 relative overflow-hidden",
						"bg-white/65 backdrop-blur-xl ring-1 ring-white/60",
						windowScrollPosition?.y > 8
							? "shadow-[0_18px_50px_-20px_rgba(90,10,224,0.35)]"
							: "shadow-[0_10px_40px_-24px_rgba(90,10,224,0.25)]",
						props.className
					])}>
					{/* aurora sheen inside the capsule */}
					<span aria-hidden className="pointer-events-none absolute -top-16 left-1/4 h-40 w-40 rounded-full bg-fuchsia-400/15 blur-3xl"/>
					<span aria-hidden className="pointer-events-none absolute -bottom-16 right-1/4 h-40 w-40 rounded-full bg-primary/15 blur-3xl"/>
					{leftArea ? leftArea : <div className="relative flex items-center gap-4">
						<div className="flex items-center gap-2.5 pl-1">
							{/* Sidebar collapse/expand toggle — sits left of the title bar */}
							<Tooltip content={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'} color="foreground" showArrow placement="bottom" delay={400} closeDelay={0}>
								<button
									type="button"
									onClick={() => setSidebarExpanded((v) => !v)}
									aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
									className="grid h-9 w-9 flex-none place-items-center rounded-full text-slate-500 ring-1 ring-slate-200/70 bg-white/70 transition-colors hover:bg-white hover:text-primary">
									<i className="fa-solid fa-bars text-sm"/>
								</button>
							</Tooltip>
							<span className="h-7 w-1.5 rounded-full bg-gradient-to-b from-primary to-fuchsia-500"/>
							<h2 className="text-xl font-semibold text-slate-800 w-fit min-w-fit tracking-tight">
								{getPageName}
							</h2>
						</div>

						{getPageName === 'Voices' && (
							<button
								type="button"
								onClick={onVoiceCloneClickHandler}
								className="group ml-2 flex items-center gap-3 rounded-full border border-slate-200 bg-white/50 py-1.5 pl-2 pr-2.5 transition-colors duration-200 hover:border-[#8a4af3] hover:bg-white">
								{/* Fixed-size animated rainbow waveform (centered on a midline, like the
								   logo). Explicit dimensions — no late-loading <img> — keep the header
								   height stable and prevent the layout shift. */}
								<style>{`@keyframes voiceWave{0%,100%{transform:scaleY(.55)}50%{transform:scaleY(1)}}`}</style>
								<span className="flex h-[26px] w-[46px] flex-none items-center justify-center gap-[1.5px]" aria-hidden="true">
									{[
										{ h: 18, c: '#8b2fc9' }, { h: 40, c: '#c026d3' }, { h: 64, c: '#ec4899' },
										{ h: 50, c: '#ef4444' }, { h: 85, c: '#f97316' }, { h: 68, c: '#eab308' },
										{ h: 100, c: '#84cc16' }, { h: 90, c: '#22c55e' }, { h: 72, c: '#10b981' },
										{ h: 94, c: '#06b6d4' }, { h: 58, c: '#3b82f6' }, { h: 40, c: '#6366f1' },
										{ h: 26, c: '#8b5cf6' }, { h: 16, c: '#a855f7' },
									].map((b, i) => (
										<span
											key={i}
											className="block w-[2px] rounded-full"
											style={{ height: `${b.h}%`, background: b.c, transformOrigin: 'center', animation: `voiceWave 1.2s ease-in-out ${i * 0.08}s infinite` }} />
									))}
								</span>
								<span className="text-left leading-tight">
									<span className="block whitespace-nowrap text-[13px] font-medium text-slate-700">Create Your Voice Clone</span>
									<span className="block whitespace-nowrap text-[11px] text-slate-500">Voice slots remaining: {authMe?.used_slot_by_user ?? 0}/{authMe?.voice_clone_slot ?? 0}</span>
								</span>
								<span className="grid h-6 w-6 flex-none place-items-center rounded-full border border-slate-300 text-slate-500 transition-colors group-hover:border-slate-600 group-hover:text-slate-700">
									<i className="fa-solid fa-arrow-right text-xs"></i>
								</span>
							</button>
						)}

					</div>}


					<div className="relative flex items-center justify-end gap-1 md:gap-4">
						{!includes([3, 5, 7], authMe?.current_plan) && (
							<TrialStatusPill status={authMe?.trial_status} endDate={authMe?.trial_end_date} animateIn={animateIn} />
						)}
						{props?.rightArea}
						<div className="flex items-center gap-4">
							<div>
								<Tooltip
									showArrow shadow
									color="foreground"
									classNames={{
										content: 'min-w-[200px] max-w-[300px] p-4 text-left justify-start',
									}}
									delay={500}
									content={<>
										<div className="w-full flex justify-center text-center items-center">
											<h3 className="font-medium  block w-full">Your Available
												Credits</h3>
										</div>
									</>}
								>
									<motion.div onClick={onOpenChange}
									     whileHover={{scale: 1.03}}
									     whileTap={{scale: 0.97}}
									     className="group cursor-pointer select-none w-full bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200/70 pl-1.5 pr-3.5 py-[7px] rounded-full flex justify-between items-center gap-2.5 shadow-sm hover:border-fuchsia-300 hover:shadow-fuchsia-200/50 transition-all">
										<span className="grid h-8 w-8 place-items-center rounded-full bg-white ring-1 ring-violet-200/70 shadow-sm">
											<i className="hgi-stroke hgi-coins-01 text-primary text-medium group-hover:scale-110 transition-transform"></i>
										</span>
										<CreditCounter value={authMe?.available_credit || 0}/>
									</motion.div>
								</Tooltip>
							</div>
						</div>
						<div className="w-px h-10 bg-divider"></div>
						<div className="flex justify-end items-center gap-4">
							<div className="text-right hidden sm:block max-w-[20dvw] truncate">
								<h2 className="text-sm text-slate-700 font-medium truncate">{authMe?.name}</h2>
								<p className="text-tiny text-slate-500 truncate">{authMe?.email}</p>
							</div>
							
							<Dropdown showArrow placement="bottom-end">
								<DropdownTrigger>
									<Avatar className="cursor-pointer" src={authMe?.avatar} title={authMe?.name}
									        key={authMe?.avatar}/>
								</DropdownTrigger>
								<DropdownMenu aria-label="Static Actions">
									<DropdownItem
										as={Link}
										href="/channels"
										startContent={<i className="eva eva-link-2-outline text-xl"/>}
										key="channels">
										Channels
									</DropdownItem>
									<DropdownItem
										as={Link}
										href="/user/profile"
										startContent={<i className="eva eva-person-outline text-xl"/>}
										key="new">
										My Profile
									</DropdownItem>
									<DropdownItem
										as={Link}
										href="/user/plans"
										startContent={<i className="eva eva-calendar-outline text-xl"/>}
										key="plans">
										Subscription
									</DropdownItem>
									<DropdownItem
										as={Link}
										href="/contact-us"
										startContent={<i className="hgi text-2xl hgi-stroke hgi-question"></i>}
										key="contact-us" className="text-primary" color="primary">
										Contact Us
									</DropdownItem>
									<DropdownItem
										onPress={() => {
											clearSessionData();
											navigate('/login')
										}}
										startContent={<i className="eva eva-log-out-outline text-xl"/>}
										key="logout" className="text-danger" color="danger">
										Logout
									</DropdownItem>
								</DropdownMenu>
							</Dropdown>
						</div>
					</div>
				</motion.header>
			</div>

			<Modal
				isDismissable={false}
				classNames={{
					base: 'rounded-3xl overflow-hidden',
					body: 'p-0'
				}}
				hideCloseButton
				isOpen={buyPaidPlanModalIsOpen}>
				<ModalContent>
					{(onClose) => (
						<ModalBody>
							{/* gradient header */}
							<div className="relative overflow-hidden bg-gradient-to-br from-primary to-fuchsia-600 px-6 pb-8 pt-7 text-white">
								<span aria-hidden className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-white/15 blur-2xl" />
								<span aria-hidden className="pointer-events-none absolute -bottom-14 left-8 h-36 w-36 rounded-full bg-fuchsia-300/30 blur-2xl" />
								<button onClick={buyPaidPlanModalOnOpenChange} aria-label="Close"
									className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25">
									<i className="hgi-stroke hgi-cancel-01 text-base"></i>
								</button>
								<div className="relative flex flex-col items-center text-center">
									<div className="mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-white/15 ring-1 ring-white/30 backdrop-blur">
										<i className="fa-solid fa-crown text-[22px] text-amber-200"></i>
									</div>
									<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">Premium</p>
									<h2 className="mt-0.5 text-[20px] font-semibold">Upgrade to a Paid Plan</h2>
								</div>
							</div>

							{/* body */}
							<div className="bg-white px-6 pb-6 pt-4">
								<div className="mx-auto -mt-2 h-24 w-[170px]">
									<DotLottieReact src="/lottie/paid-plan.lottie" loop autoplay />
								</div>
								<p className="text-center text-[13.5px] leading-relaxed text-slate-600">
									Unlock <strong className="font-semibold text-slate-800">Premium Voice Clone</strong> and more. Pick the plan that fits and start creating today.
								</p>

								<div className="mt-4 flex flex-col gap-2.5">
									{['Clone your own custom voices', 'Access premium, studio-grade voices', 'Higher limits & priority processing'].map((perk) => (
										<div key={perk} className="flex items-center gap-2.5 text-[13px] text-slate-700">
											<span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-primary/10 text-primary">
												<i className="fa-solid fa-check text-[10px]"></i>
											</span>
											{perk}
										</div>
									))}
								</div>

								<Button
									className="mt-5 h-12 w-full rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 text-[15px] font-semibold text-white shadow-sm transition hover:shadow-md"
									onPress={() => { navigate("/user/plans"); buyPaidPlanModalOnOpenChange(); }}>
									<i className="fa-solid fa-rocket text-[15px]"></i> View plans &amp; upgrade
								</Button>
								<button onClick={buyPaidPlanModalOnOpenChange}
									className="mt-2.5 w-full text-center text-[12.5px] font-medium text-slate-400 transition hover:text-slate-600">
									Maybe later
								</button>
							</div>
						</ModalBody>
					)}
				</ModalContent>
			</Modal>

		</>
	)
}
